import { Button, Tooltip } from 'antd';
import { FileTextOutlined, UserOutlined } from '@ant-design/icons';
import { IoEyeOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import type { UploadRow, UploadStage } from '@/models/upload';
import styles from './UploadRowActions.module.css';

interface UploadRowActionsProps {
  upload: UploadRow;
  onPatientClick: () => void;
}

// R15: the study is viewable once its DICOMs are in (AI stage), and
// reportable once AI is done or has failed.
const OPEN_STUDY_STAGES: UploadStage[] = ['AI', 'DONE', 'FAILED'];
const OPEN_REPORT_STAGES: UploadStage[] = ['DONE', 'FAILED'];

const NO_STUDY_TOOLTIP = 'Available once the study is created';

export default function UploadRowActions({
  upload,
  onPatientClick,
}: UploadRowActionsProps) {
  const navigate = useNavigate();
  const { studyUuid, stage } = upload;
  const canOpenStudy = !!studyUuid && OPEN_STUDY_STAGES.includes(stage);
  const canOpenReport = !!studyUuid && OPEN_REPORT_STAGES.includes(stage);
  const canAssignPatient = !!studyUuid;

  let studyTooltip = 'Open study';
  if (!studyUuid) studyTooltip = NO_STUDY_TOOLTIP;
  else if (!canOpenStudy) studyTooltip = 'Available once DICOMs are processed';

  let reportTooltip = 'Open report';
  if (!studyUuid) reportTooltip = NO_STUDY_TOOLTIP;
  else if (!canOpenReport)
    reportTooltip = 'Available once AI processing finishes';

  let patientTooltip = 'Reassign patient';
  if (!canAssignPatient) patientTooltip = NO_STUDY_TOOLTIP;
  else if (upload.needsPatientAssignment)
    patientTooltip = 'Needs patient — link or create';

  const goToStudy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/studies/${studyUuid}`);
  };

  const goToReport = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/reports/${studyUuid}`);
  };

  const handlePatient = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPatientClick();
  };

  // The yellow warning only fires when the patient can actually be assigned.
  const patientNeedsAttention =
    canAssignPatient && upload.needsPatientAssignment;

  return (
    <div
      className={styles.actions}
      role="toolbar"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Tooltip title={studyTooltip}>
        <Button
          type="text"
          aria-label="Open study"
          icon={<IoEyeOutline />}
          onClick={goToStudy}
          disabled={!canOpenStudy}
        />
      </Tooltip>
      <Tooltip title={reportTooltip}>
        <Button
          type="text"
          aria-label="Open report"
          icon={<FileTextOutlined />}
          onClick={goToReport}
          disabled={!canOpenReport}
        />
      </Tooltip>
      <Tooltip title={patientTooltip}>
        <Button
          type="text"
          aria-label={patientTooltip}
          icon={
            <UserOutlined
              className={
                patientNeedsAttention ? styles.patientWarningIcon : undefined
              }
            />
          }
          onClick={handlePatient}
          disabled={!canAssignPatient}
        />
      </Tooltip>
    </div>
  );
}
